import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useBackend } from '../backend/BackendContext';
import {
  CellarNotFoundError,
  createCellarStore,
  type CellarChange,
  type CellarStore,
} from './cellar-store';
import { StorageAccessDeniedError } from './fake-storage';
import type { Cellar } from '../domain/cellar';

/** Why a cellar could not be opened: it does not exist or is not ours to see, or anything else. */
export type CellarLoadFailure = 'not-found' | 'failed';

interface CellarStoreContextValue {
  cellar: Cellar | undefined;
  failure: CellarLoadFailure | undefined;
  apply: (change: CellarChange) => Promise<Cellar>;
}

const CellarStoreContext = createContext<CellarStoreContextValue | undefined>(undefined);

/**
 * Loads one cellar from the backend's storage and keeps it current for the screens inside.
 * `createIfMissing` is true only for the person's own cellar (created at first sign-in, T073).
 * A missing or denied cellar is reported as 'not-found', so the screen shows the same plain
 * "not found" page either way (contracts/storage-and-permissions.md).
 */
export function CellarStoreProvider({
  cellarId,
  createIfMissing,
  children,
}: {
  cellarId: string;
  createIfMissing: boolean;
  children: ReactNode;
}) {
  const { storage } = useBackend();
  const store = useMemo<CellarStore>(
    () => createCellarStore(storage, cellarId, { createIfMissing }),
    [storage, cellarId, createIfMissing],
  );
  const [cellar, setCellar] = useState<Cellar | undefined>(undefined);
  const [failure, setFailure] = useState<CellarLoadFailure | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setCellar(undefined);
    setFailure(undefined);
    store.load().then(
      (loaded) => {
        if (!cancelled) setCellar(loaded);
      },
      (error: unknown) => {
        if (cancelled) return;
        const missing =
          error instanceof CellarNotFoundError || error instanceof StorageAccessDeniedError;
        setFailure(missing ? 'not-found' : 'failed');
      },
    );
    const unsubscribe = store.onChange((next) => {
      if (!cancelled) {
        setCellar(next);
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [store]);

  const value: CellarStoreContextValue = {
    cellar,
    failure,
    apply: (change) => store.apply(change),
  };

  return <CellarStoreContext.Provider value={value}>{children}</CellarStoreContext.Provider>;
}

export function useCellarStore(): CellarStoreContextValue {
  const value = useContext(CellarStoreContext);
  if (!value) {
    throw new Error('useCellarStore() must be used within a <CellarStoreProvider>');
  }
  return value;
}
